/** Presentational grid task row: title, optional checkbox, and project chip. */
import { Box, Checkbox, Typography } from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import {
  gridTaskItemSx,
  gridTaskItemDenseSx,
  gridTaskItemRoomySx,
  planTaskCheckboxSx,
  GRID_TASK_DRAG_OPACITY,
} from '@/components/ui/taskVisualTokens';
import {
  buildTaskRowTitleTrailingRowSx,
  buildTaskRowTitleSx,
  buildTaskRowTrailingSx,
} from '@/components/ui/buildTaskRowTitleTrailingSx';
import TagChip from '@/components/ui/TagChip';
import { buildGridTaskTitleSx } from './buildGridTaskTitleSx';

const WEEK_MONTH_CHIP_WRAP_SX = {
  minWidth: 0,
  flexShrink: 1,
  maxWidth: '100%',
};

const resolveItemSx = (dense, roomy) => {
  if (dense) return gridTaskItemDenseSx;
  if (roomy) return gridTaskItemRoomySx;
  return gridTaskItemSx;
};

const buildGridTaskBoxSx = (dense, roomy, isDragging, canDrag) => ({
  ...resolveItemSx(dense, roomy),
  ...(roomy ? buildTaskRowTitleTrailingRowSx() : {}),
  opacity: isDragging ? GRID_TASK_DRAG_OPACITY : 1,
  cursor: canDrag ? 'grab' : 'default',
});

const buildGridTaskDragProps = (canDrag, setNodeRef, listeners, attributes) =>
  canDrag ? { ref: setNodeRef, ...listeners, ...attributes, tabIndex: -1 } : {};

const buildTitleSx = (dense, completed, isMainTask, roomy) => ({
  ...buildGridTaskTitleSx(dense, completed, isMainTask),
  ...(roomy ? buildTaskRowTitleSx() : {}),
});

const buildChipWrapSx = (roomy) =>
  roomy ? buildTaskRowTrailingSx({ marginLeftAuto: true }) : WEEK_MONTH_CHIP_WRAP_SX;

const GridTaskItemContent = ({
  resolvedTitle,
  primaryProject,
  projectLabel,
  task,
  isMainTask,
  showComplete,
  onToggleComplete,
  dense,
  roomy,
  canDrag,
  setNodeRef,
  listeners,
  attributes,
  isDragging,
  onDoubleClick,
  keyboardActive = false,
  keyboardItemId,
}) => {
  const completed = Boolean(task?.completed);

  return (
    <Box
      {...buildGridTaskDragProps(canDrag, setNodeRef, listeners, attributes)}
      id={keyboardItemId}
      role={keyboardItemId ? 'option' : undefined}
      aria-selected={keyboardItemId ? keyboardActive : undefined}
      data-testid="grid-task-item"
      data-dense={dense ? 'true' : undefined}
      data-roomy={roomy ? 'true' : undefined}
      onDoubleClick={onDoubleClick}
      sx={{
        ...buildGridTaskBoxSx(dense, roomy, isDragging, canDrag),
        ...(keyboardActive
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 1,
            }
          : {}),
      }}
      title={resolvedTitle}
    >
      {showComplete ? (
        <Checkbox
          checked={completed}
          tabIndex={-1}
          onChange={(e, checked) => onToggleComplete?.(checked)}
          onPointerDown={(e) => e.stopPropagation()}
          size="small"
          sx={planTaskCheckboxSx}
        />
      ) : null}
      {isMainTask ? (
        <FlagOutlinedIcon
          data-testid="grid-task-main-icon"
          fontSize="inherit"
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        />
      ) : null}
      <Typography
        component="span"
        data-testid="grid-task-title"
        sx={buildTitleSx(dense, completed, isMainTask, roomy)}
      >
        {resolvedTitle}
      </Typography>
      {primaryProject ? (
        <Box data-testid="grid-task-project-chip-wrap" sx={buildChipWrapSx(roomy)}>
          <TagChip
            tag={primaryProject}
            label={projectLabel}
            dense={dense}
            data-testid="grid-task-project-chip"
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default GridTaskItemContent;
